import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import first from 'lodash.first';

import getShortName from 'helpers/getShortName';

import { MAX_COUNTRY_NAME } from 'config';
import CountryFlag from 'components/base/CountryFlag';
import TooltipWrapper from 'components/base/TooltipWrapper';
import { IIterator, ICountryFlagProps } from 'types/shared';
import {
  ICountryDataItem,
  IEntities,
} from 'components/data-containers/KPIs/types';

const ListWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 1em;
  flex-wrap: wrap;
`;

const CountryListItem = styled.li<IIterator>`
  display: flex;
  align-items: baseline;
  color: ${({ dataColor }): string => dataColor};
  font-weight: ${(props): string => (props.useHighlight ? '600' : 'normal')};
`;

const CountryList = styled.ol<IIterator>`
  flex: 0 0 auto;
  padding-right: ${({ theme }): string => theme.spacing.medium};
  list-style: none;
  padding-left: 0;
  width: ${({ compareMode }): string => (compareMode ? 'fit-content' : '100%')};

  &:first-child {
    ${CountryListItem} {
      &:before {
        content: '#' attr(value);
        display: inline-block;
        min-width: 2rem;
        margin-right: 0.5rem;
        text-align: right;
        color: ${({ theme }): string => theme.NightBlue};
      }
    }
  }

  &:last-child {
    padding-right: 0;
  }
`;

const CountryName = styled.span<IIterator>`
  color: ${({ dataColor }): string => dataColor};
`;

const EmptyListItem = styled.div<IIterator>`
  color: ${({ dataColor }): string | undefined => dataColor};
  margin-right: ${({ theme }): string => theme.spacing.medium};
`;

const ToggleListButton = styled.button`
  appearance: none;
  font-weight: 400;
  color: ${({ theme }): string => theme.AccentGreen};
  text-align: left;
  flex: 1 0 auto;
  padding: 0;
  margin-top: ${({ theme }): string => theme.spacing.xsmall};

  &:hover,
  &:active {
    text-decoration: underline;
  }
`;

interface ICountryRaw {
  countryId: string;
  countryName: string;
  data: ICountryDataItem[];
}

interface IMappedCountry extends ICountryRaw {
  compareArrayIndex: number;
  index: number;
}

interface ITopCountriesListProps {
  fulfilled: boolean;
  // Pass the CountryID of the country you want to highlight, e.g. US
  highlight?: string;
  compareMode?: string;
  dataColors: string[];
  compareArray: string[];
  entities: IEntities[];
  sortMetric: 'twtd' | 'rtd' | 'lw';
  // Should a toggle button to toggle the visibility of the list be visible
  hasListToggle: boolean;
  countryFlags: any[];
}

const TopCountriesList = (props: ITopCountriesListProps): JSX.Element => {
  const {
    fulfilled,
    highlight,
    compareMode,
    dataColors,
    compareArray,
    entities = [],
    sortMetric,
    hasListToggle,
    countryFlags,
  } = props;

  // Make sure you can toggle the visibility of the list on small screens.
  const [showList, setShowList] = useState(hasListToggle);

  function handleToggleList(): void {
    setShowList(!showList);
  }

  useEffect(() => {
    setShowList(hasListToggle);
  }, [hasListToggle]);

  return (
    <>
      {fulfilled && (
        <>
          {showList && (
            <ListWrapper
              data-testid={`${sortMetric}-top-countries`}
              data-pw={`${sortMetric}-top-countries`}
            >
              {/** In entity compare you might have to loop over and show more than 1 list */}
              {entities?.map(
                (entity: IEntities, entityIndex: number): JSX.Element => {
                  const countries = entity.data;
                  const entityId = entity.entity.id;

                  /**
                   * We can not depend on the compareArray to pass a color to the country names,
                   * because the API returns sorted data.
                   * We do a lookup to find the index in the compareArray
                   */
                  const mappedCountries = countries.map(
                    (item: ICountryRaw, index: number): IMappedCountry => {
                      return {
                        ...item,
                        index: index,
                        compareArrayIndex: compareArray.findIndex(
                          (compareItem: string) => {
                            return item.countryId === compareItem;
                          }
                        ),
                      };
                    }
                  );

                  if (mappedCountries.length < 1) {
                    return (
                      <EmptyListItem key={`empty-entity-${entityId}`}>
                        –
                      </EmptyListItem>
                    );
                  }

                  return (
                    <CountryList
                      key={`country-list-${entityId}`}
                      compareMode={compareMode}
                    >
                      {mappedCountries.map(
                        (
                          country: IMappedCountry,
                          index: number
                        ): JSX.Element | null => {
                          const { countryId, countryName, compareArrayIndex } =
                            country;

                          if (highlight) {
                            const indexOfHighlightedCountry =
                              mappedCountries.findIndex(
                                (country: IMappedCountry): boolean =>
                                  country.countryId === highlight
                              );

                            const highlightedIsLastItemInList =
                              indexOfHighlightedCountry ===
                              mappedCountries.length - 1;

                            if (highlightedIsLastItemInList) {
                              // The highlighted country is the last item in the list. We need to remove the item before that.
                              if (index === indexOfHighlightedCountry - 1) {
                                return null;
                              }
                            } else {
                              // Otherwise it is higher in the list so we need to remove the last country item, to make sure it always shows 4 items.
                              if (index === mappedCountries.length - 1) {
                                return null;
                              }
                            }
                          } else {
                            // If there is no highlighted country, always remove the first item of the list
                            // because that is the same as the country shown by the TopCountry component
                            if (index === 0) {
                              return null;
                            }
                          }

                          // Find the right flag for this list item
                          const flag = countryFlags.find(
                            (listItem: ICountryFlagProps): boolean =>
                              country.countryId === listItem.id
                          );

                          /**
                           * Should this List item be highlighted?
                           * Used to show a result matching a country filter
                           */
                          const useHighlight = country.countryId === highlight;

                          /**
                           * We need to only define a color if the country was found
                           * in the filtered list of countries
                           */
                          let color = undefined;
                          if (compareMode === 'entity') {
                            color = dataColors[entityIndex];
                          } else if (compareMode === 'country') {
                            color = dataColors[compareArrayIndex];
                          }
                          const countryDisplayName =
                            compareMode === 'entity'
                              ? countryId
                              : getShortName(countryName);

                          return (
                            <CountryListItem
                              key={`country-list-${entityId}-${countryId}`}
                              value={
                                (first(country.data) as ICountryDataItem)
                                  .countryRank
                              }
                              dataColor={color}
                              useHighlight={useHighlight}
                              data-testid={`country-list-${entityId}-${countryId}`}
                              data-pw={`territory-kpis-${sortMetric}-country-list-entity-${entityIndex}`}
                              data-country={countryId}
                            >
                              {!compareMode && (
                                <CountryFlag {...flag} small={true} />
                              )}
                              <TooltipWrapper
                                condition={
                                  countryName.length > MAX_COUNTRY_NAME
                                }
                                tooltipContent={countryName}
                              >
                                <CountryName>{countryDisplayName}</CountryName>
                              </TooltipWrapper>
                            </CountryListItem>
                          );
                        }
                      )}
                      {/* If there are less than 5 top countries,
                      we want to fill the list up to 5 to indicate that there
                      aren't any more countries for this entity*/}
                      {mappedCountries.length < 5 &&
                        Array(5 - mappedCountries.length)
                          .fill(0)
                          .map((_: any, index: number) => (
                            <CountryListItem
                              value={mappedCountries.length + index + 1}
                              key={`filler-entity-${index}`}
                            >
                              –
                            </CountryListItem>
                          ))}
                    </CountryList>
                  );
                }
              )}
            </ListWrapper>
          )}
          {!hasListToggle && (
            <ToggleListButton onClick={handleToggleList}>
              {showList ? 'Hide' : 'Show'} top list
            </ToggleListButton>
          )}
        </>
      )}
      {!fulfilled && <div />}
    </>
  );
};

export default TopCountriesList;
